/**
 * CorporateAgreementRow — view + inline-edit for one corporate
 * agreement entry on a patient. Mirrors the Insurance row shape but
 * with `corporate*` fields and no `isPrimary` flag (Flutter doesn't
 * model a primary sponsor on corporates).
 *
 * Edit form (Flutter `edit_corporate_agreement_widget.dart` order):
 *   1. Corporate card photos (front / back)
 *   2. Corporate sponsor (typeahead)
 *   3. Member ID
 *   4. Eligibility / Expiration dates
 *   5. Discount type toggle + per-billing-group breakdown
 *      (corporate uses `discountType`; billingGroups ship
 *      {billingGroupId, category, value} — NO maximumCopayment, per
 *      the 2026-06-05 /api/Patients/PatientCorporate dev curl)
 *   6. Confirmation checkbox (verbatim copy)
 *
 * Card photos + discount breakdown were the F-PAT-243 deep-edit gap,
 * now built via the shared `PayerPhotoUploader` + `PayerCopayTable`.
 */

import { useState } from 'react';
import { Pencil, Trash2, X, Search } from 'lucide-react';
import { RecordRowCard } from '@/shared/ui/components/record-row-card';
import { Button, FieldLabel, StatusChip } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { DatePickerField } from '@/shared/ui/components';
import { Switch } from '@scripsteam/scrips-signal-ds';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useSearchCorporateSponsors } from '@/patients/services/use-search-corporate-sponsors';
import { PayerPhotoUploader } from '@/shared/ui/components/payer-photo-uploader';
import { PayerCopayTable } from '@/shared/ui/components/payer-copay-table';
import { IMAGE_AREAS } from '@/services/common/use-upload-image';
import { cn } from '@/lib/utils';
import {
  PayerReadLines,
  PayerSearchOption,
  payerTitle,
} from '@/patients/lib/payer-read-lines';
import type { CorporateList, PatientPayerBillingGroup } from '@/types/patient-api';
import type { BillingGroup } from '@/shared/types/billing';

/** UI flag layered on the wire type — never sent. */
export interface CorporateRow extends CorporateList {
  isEdit?: boolean;
}

export interface CorporateAgreementRowProps {
  row: CorporateRow;
  isOnly: boolean;
  organizationId: string;
  /** Org billing groups for the discount breakdown table. */
  billingGroups: BillingGroup[];
  /** Org currency code for the "Fixed, {code}" discount pill. */
  currencyCode: string;
  readOnly?: boolean;
  onChange: (next: CorporateRow) => void;
  onCancel: () => void;
  onRemove: () => void;
}

export function CorporateAgreementRowComponent({
  row,
  isOnly,
  organizationId,
  billingGroups,
  currencyCode,
  readOnly,
  onChange,
  onCancel,
  onRemove,
}: CorporateAgreementRowProps) {
  if (row.isDeleted) return null;
  return row.isEdit ? (
    <EditCard
      row={row}
      isOnly={isOnly}
      organizationId={organizationId}
      billingGroups={billingGroups}
      currencyCode={currencyCode}
      readOnly={readOnly}
      onChange={onChange}
      onCancel={onCancel}
      onRemove={onRemove}
    />
  ) : (
    <ReadCard
      row={row}
      isOnly={isOnly}
      readOnly={readOnly}
      onEdit={() => onChange({ ...row, isEdit: true })}
      onRemove={onRemove}
    />
  );
}

function ReadCard({
  row,
  isOnly,
  readOnly,
  onEdit,
  onRemove,
}: {
  row: CorporateRow;
  isOnly: boolean;
  readOnly?: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const sponsorName = row.corporateProvider
    ? payerTitle(row.corporateProvider)
    : 'Unknown sponsor';
  // The approved design's PaRecordRowCard: the sponsor with its Verified chip, member ID and eligibility as the quiet
  // lines, edit and remove on the right. Same buttons, handlers and disabled rule as before.
  return (
    <RecordRowCard
      title={sponsorName}
      chips={
        row.isVerified ? (
          <StatusChip semantic="success" tone="soft">
            Verified
          </StatusChip>
        ) : null
      }
      lines={
        // Corporate omits the "Insurance: … TPA: …" codes line (Flutter corporate card) — Member ID + eligibility only.
        <PayerReadLines
          policyNumber={row.policyNumber}
          eligibilityStart={row.eligiblityStartDate}
          expirationDate={row.expirationDate}
          expiredLabel="Corporate Card Expired"
        />
      }
      actions={
        !readOnly ? (
          <>
            <button type="button" onClick={onEdit} className="icon-action" aria-label="Edit corporate agreement">
              <Pencil className="size-4" />
            </button>
            <button
              type="button"
              onClick={onRemove}
              disabled={isOnly}
              className={cn('icon-action', isOnly && 'opacity-40 cursor-not-allowed hover:bg-transparent')}
              aria-label="Remove corporate agreement"
            >
              <Trash2 className="size-4" />
            </button>
          </>
        ) : null
      }
    />
  );
}

function EditCard({
  row,
  isOnly,
  organizationId,
  billingGroups,
  currencyCode,
  readOnly,
  onChange,
  onCancel,
  onRemove,
}: {
  row: CorporateRow;
  isOnly: boolean;
  organizationId: string;
  billingGroups: BillingGroup[];
  currencyCode: string;
  readOnly?: boolean;
  onChange: (next: CorporateRow) => void;
  onCancel: () => void;
  onRemove: () => void;
}) {
  const update = (patch: Partial<CorporateRow>) => onChange({ ...row, ...patch });

  const [sponsorQuery, setSponsorQuery] = useState(
    row.corporateProvider ? payerTitle(row.corporateProvider) : '',
  );
  const debouncedQuery = useDebounce(sponsorQuery, 250);
  const { data: sponsors } = useSearchCorporateSponsors({
    organizationId,
    query: debouncedQuery,
  });
  const [sponsorOpen, setSponsorOpen] = useState(false);

  // Discount validation mirrors insurance: selecting billing groups
  // requires a discount type AND a value on each (Flutter onSave guard).
  const selectedGroups = row.billingGroups ?? [];
  const discountValid =
    selectedGroups.length === 0 ||
    (!!row.discountType && selectedGroups.every((g) => !!g.value));

  const canDone =
    !!row.corporateProvider?.id &&
    !!row.policyNumber?.trim() &&
    !!row.expirationDate &&
    discountValid;

  return (
    // The row opened into its fields (design PaRecordRowCard `editing`): Done (primary) then Remove (tertiary).
    <RecordRowCard
      editing
      title={row.corporateId ? 'Edit corporate agreement' : 'New corporate agreement'}
      actions={
        <button type="button" onClick={onCancel} className="icon-action" aria-label="Cancel edit">
          <X className="size-4" />
        </button>
      }
      editActions={
        <>
          <Button type="button" size="sm" onClick={() => update({ isEdit: false })} disabled={readOnly || !canDone}>
            Done
          </Button>
          <Button type="button" variant="tertiary" size="sm" onClick={onRemove} disabled={isOnly || readOnly}>
            Remove
          </Button>
        </>
      }
    >

      {/* Card photos first (Flutter field order). Area = PATIENT
          (Flutter `imageAreaPatient`; 2026-06-05 UploadImage curl). */}
      <div>
        <FieldLabel >
          Corporate card photos
        </FieldLabel>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <PayerPhotoUploader
            url={row.frontPhotoUrl ?? null}
            label="Front Photo"
            side="front"
            titleNoun="corporate"
            area={IMAGE_AREAS.PATIENT}
            disabled={readOnly}
            onUploaded={(url) => update({ frontPhotoUrl: url })}
            onClear={() => update({ frontPhotoUrl: null })}
          />
          <PayerPhotoUploader
            url={row.backPhotoUrl ?? null}
            label="Back Photo"
            side="back"
            titleNoun="corporate"
            area={IMAGE_AREAS.PATIENT}
            disabled={readOnly}
            onUploaded={(url) => update({ backPhotoUrl: url })}
            onClear={() => update({ backPhotoUrl: null })}
          />
        </div>
      </div>

      <div>
        <FieldLabel >
          Corporate sponsor <span className="text-status-error">*</span>
        </FieldLabel>
        <div className="relative mt-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-hint" />
          <TextField
            value={sponsorQuery}
            onChange={(e) => {
              setSponsorQuery(e.target.value);
              setSponsorOpen(true);
            }}
            onFocus={() => setSponsorOpen(true)}
            onBlur={() => setTimeout(() => setSponsorOpen(false), 150)}
            placeholder="Start typing a sponsor name (min 2 chars)…"
            disabled={readOnly}
            inputClassName="pl-8"
          />
          {sponsorOpen && (sponsors?.length ?? 0) > 0 ? (
            <ul className="absolute left-0 right-0 top-full mt-1 z-10 max-h-72 overflow-auto scrollbar-hide rounded-md border border-border-default bg-surface-card shadow-md">
              {(sponsors ?? []).slice(0, 12).map((s) => (
                <li key={s.id ?? s.payerName ?? ''}>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      // Store the FULL PayerDetails object from the search
                      // response (payerCompanyId, payerAgreementId,
                      // relatedTpa[], etc. — required by the save endpoint).
                      update({
                        corporateProviderId: s.id ?? null,
                        corporateProvider: s,
                      });
                      setSponsorQuery(payerTitle(s));
                      setSponsorOpen(false);
                    }}
                    className="block w-full px-3 py-2 text-left hover:bg-grey-100"
                  >
                    <PayerSearchOption provider={s} />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>

      <div>
        <FieldLabel >
          Member ID / Employee number <span className="text-status-error">*</span>
        </FieldLabel>
        <TextField
          value={row.policyNumber ?? ''}
          onChange={(e) => update({ policyNumber: e.target.value })}
          placeholder="Enter employee number"
          maxLength={50}
          className="mt-1"
          disabled={readOnly}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <FieldLabel >
            Eligibility start
          </FieldLabel>
          <DatePickerField
            value={(row.eligiblityStartDate ?? '').slice(0, 10)}
            onChange={(v) =>
              update({
                eligiblityStartDate: v ? `${v}T00:00:00.000` : null,
              })
            }
            className="mt-1"
            disabled={readOnly}
          />
        </div>
        <div>
          <FieldLabel >
            Expiration <span className="text-status-error">*</span>
          </FieldLabel>
          <DatePickerField
            value={(row.expirationDate ?? '').slice(0, 10)}
            onChange={(v) =>
              update({
                expirationDate: v ? `${v}T00:00:00.000` : null,
              })
            }
            className="mt-1"
            disabled={readOnly}
          />
        </div>
      </div>

      {/* Discount type toggle + per-billing-group breakdown. Corporate
          has NO Max-Copay column and ships {billingGroupId, category,
          value} only (2026-06-05 PatientCorporate curl). */}
      {billingGroups.length > 0 ? (
        <PayerCopayTable<PatientPayerBillingGroup>
          label="Discount"
          showMaxCopay={false}
          billingGroups={billingGroups}
          selected={row.billingGroups ?? []}
          onChange={(next) => update({ billingGroups: next })}
          copayType={row.discountType}
          onCopayTypeChange={(next) => update({ discountType: next })}
          currencyCode={currencyCode}
          disabled={readOnly}
          makeGroup={(g) => ({
            billingGroupId: g.id,
            category: g.category,
            value: 0,
          })}
        />
      ) : null}

      <div className="flex items-center justify-between gap-3 pt-2 border-t border-border-default">
        {/* Verbatim Flutter copy — edit_corporate_agreement_widget.dart.
            The corporate variant says "corporate policy" (insurance says
            "insurance policy"). Conversion contract requires verbatim
            confirmation strings, not paraphrase (was "I've verified the
            details match the card"). */}
        {/* TODO(i18n): compliance-adjacent copy hardcoded to match Flutter
            verbatim. If it becomes legally-managed, source from config/i18n. */}
        <p className="text-sm text-text-primary flex-1">
          I confirm that the data entered matches that on the card. This corporate policy is accepted at this clinic.
        </p>
        <Switch
          checked={!!row.isVerified}
          onCheckedChange={(checked) => update({ isVerified: checked })}
          disabled={readOnly}
          aria-label="I confirm that the data entered matches that on the card. This corporate policy is accepted at this clinic."
        />
      </div>

    </RecordRowCard>
  );
}
